/**
 * Codebox Component
 * 
 * A simple component for displaying and formatting code blocks with syntax highlighting.
 * Used in documentation, tutorials, and the Hook Testing Dashboard.
 * 
 * @author Meduler Team
 * @version 1.0.0
 */
import React from 'react';
import { cn } from '@/lib/utils';

// Define the props for the Codebox component
interface CodeboxProps {
  /** The language to use for syntax highlighting */
  language?: string;
  /** The code content to display */
  children: string;
  /** Optional additional className */
  className?: string;
  /** Whether to show line numbers (default: true) */
  showLineNumbers?: boolean;
  /** Whether to enable copying to clipboard (default: true) */
  enableCopy?: boolean;
}

/**
 * Codebox component for displaying formatted code snippets
 * 
 * @example
 * <Codebox language="tsx">
 *   const Example = () => <div>Hello World</div>;
 * </Codebox>
 */
export const Codebox: React.FC<CodeboxProps> = ({
  language = 'typescript',
  children,
  className,
  showLineNumbers = true,
  enableCopy = true,
}) => {
  const code = children.trim();
  const [copied, setCopied] = React.useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  
  const lines = code.split('\n');
  
  return (
    <div className={cn(
      "relative rounded-md overflow-hidden bg-gray-800 text-gray-200 font-mono text-sm",
      className
    )}>
      {/* Language indicator */}
      {language && (
        <div className="absolute top-0 right-0 px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded-bl">
          {language}
        </div>
      )}
      
      {/* Copy button */}
      {enableCopy && (
        <button
          className="absolute top-2 right-12 p-1 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 text-xs"
          onClick={handleCopy}
          aria-label="Copy code to clipboard"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      )}
      
      {/* Code content */}
      <div className="p-4 pt-6 overflow-x-auto">
        <pre className="font-mono text-sm">
          <code>
            {showLineNumbers ? (
              lines.map((line, index) => (
                <div key={index} className="table-row">
                  <span className="table-cell pr-4 text-gray-500 select-none text-right">
                    {index + 1}
                  </span>
                  <span className="table-cell">
                    {line || ' '}
                  </span>
                </div>
              ))
            ) : (
              code
            )}
          </code>
        </pre>
      </div>
    </div>
  );
};

export default Codebox;
